/**
 * O16 Product Gallery
 * Custom element <o16-product-gallery> — tab switching, thumbnail selection, video playback.
 *
 * Architecture:
 *   - Custom element (connectedCallback / disconnectedCallback) for idempotent init.
 *   - Works independently of the base theme's swiper/main-product.js.
 *   - No console.* in production.
 *   - Respects prefers-reduced-motion.
 *   - Accessible: aria-pressed on tabs + thumbs; focus-visible rings kept via CSS.
 *
 * Tabs:
 *   - "all"   → every image item (data-in-all), in native backend order.
 *   - "inuse" / "studio" → items whose data-cat matches.
 *   - "motion" → video item(s). Video autoplays muted + loops when its item is active,
 *     and pauses when the user switches away.
 *
 * Swipe / drag (main image):
 *   - Pointer Events (touch + mouse + pen in one code path) on [data-o16-main-frame].
 *   - Horizontal drag past SWIPE_THRESHOLD_PX navigates to the adjacent item WITHIN
 *     the current tab's visible set (the same set the thumbnail row shows), via the
 *     same _activateItem() method thumbnail clicks call — tabs, thumbnails, aria
 *     state and video autoplay all stay in sync automatically.
 *   - Clamps at the ends (no loop, matching thumbnail nav); a tap/click still works.
 *   - `touch-action: pan-y` on the main frame (CSS) lets vertical page-scroll pass
 *     through natively — the browser fires us a pointercancel when it takes over.
 *
 * Swipe hint (mobile only):
 *   - Mirrors the mobile product-card carousel's "Swipe →" hint (see
 *     o16-card-carousel.js / o16-card-carousel.css). Injected into the main
 *     frame only when the gallery actually has more than one item to swipe to
 *     (a single-image product shows no hint). CSS scopes it to ≤749px —
 *     desktop relies on thumbnails/clicking/arrow keys instead.
 *   - Fades out (once) the first time a genuine swipe navigates — a tap or a
 *     thumbnail click never reaches _navigateSwipe, so it doesn't hide the hint.
 *
 * Arrow-key navigation (desktop):
 *   - ArrowLeft/ArrowRight step to the previous/next item via the same
 *     _navigateSwipe()/_activateItem() path as touch-swipe, so tabs,
 *     thumbnails, video autoplay and variant state stay in sync. Clamps at
 *     both ends (no loop). No visual marker for this — the "Swipe →" hint
 *     stays mobile-only by design. Guarded against hijacking typing in form
 *     controls (inputs, the quantity field, selects, contenteditable) and
 *     against modifier-key combos (ctrl/alt/meta).
 *
 * Variant sync:
 *   - Listens for the base-theme "variant:change" CustomEvent (dispatched by
 *     VariantSelects / VariantRadios in global.js on every variant selection).
 *   - When the chosen variant has a featured_media, jumps the gallery to that
 *     media item (activates it as the primary image) without removing it from
 *     the normal navigation flow. The item stays in its tab (All / Studio /
 *     In-use) so the visitor can page away and back to it naturally.
 *   - If the item belongs to a tab that isn't currently open, the gallery
 *     switches to the most appropriate tab (prefers "all" if the item is in it,
 *     otherwise the item's own category tab).
 *   - Gallery IDs embed the section ID ("o16-gallery-<sectionId>"), and the
 *     event carries "sectionId", so each gallery only responds to its own
 *     variant-radios (safe for pages with multiple product sections).
 *
 * Tab row fit-to-one-line (owner 2026-07-12, right-padding fix round-2):
 *   - Category tabs must NEVER wrap onto a second row (o16-product-gallery.css
 *     enforces flex-shrink:0 + white-space:nowrap + no flex-wrap). A product
 *     with many categories (e.g. Selection/In-use/Studio/Pages/Dividers/Motion)
 *     can still overflow the tabs container's fixed-width box (constrained by
 *     the 0.75rem left+right insets) at the base size.
 *   - _fitTabsAtScale() shrinks a single CSS custom property, --o16-tab-fit
 *     (which drives font-size + horizontal padding + icon size in the CSS),
 *     re-measuring after each step, until the row's real content width
 *     (first tab's left edge to last tab's right edge) fits within an
 *     EXPLICIT reserved target — TAB_FIT_RIGHT_RESERVE_PX px narrower than
 *     the container's own available width — so the last tab's right edge
 *     always clears the frame's right edge by MORE than the 0.75rem inset
 *     alone would guarantee, deliberately ≥ the left gap on every device
 *     (a real-phone report found the naive "fits the container exactly"
 *     version reading tight/asymmetric — see git history for the round-1
 *     version and why it wasn't reliable across font-rendering engines).
 *     A product whose tabs already fit at full size is untouched (this is a
 *     pure "shrink only if needed" routine — same spirit as the fit-to-
 *     one-line pattern in o16-header-taglines.js / o16-collection-intro.js).
 *   - Two passes: (1) shrink with icons still visible, down to
 *     TAB_FIT_MIN_ICONS_KEPT (~5.6px label — still comfortably legible);
 *     (2) only if that's not enough room, hide the decorative tab icons
 *     (≤749px only, via CSS) to buy back ~16px/tab, then re-attempt down to
 *     the absolute floor TAB_FIT_MIN. The label text is the essential,
 *     always-visible part — the icon is not.
 *   - Runs per gallery instance (each <o16-product-gallery> custom element
 *     scopes its own connectedCallback/querySelector), on connect, on
 *     document.fonts.ready, on window load, and on debounced resize.
 *   - The section renders TWICE on the page (main-product.liquid's
 *     .main-product-desktop-section-wrapper / .main-product-mobile-section-
 *     wrapper — one pair per gallery_type / mobile_gallery_type — shown/
 *     hidden purely via a CSS `display:none` media query at 750px, not JS).
 *     A CSS-hidden instance is NOT "about to become visible" on most
 *     sessions (e.g. a phone that never crosses 750px), so it is detected
 *     via `offsetParent === null` and left alone entirely — no rAF polling —
 *     rather than spinning every frame for the whole page visit. The window
 *     'resize' listener (already armed on both instances) is what re-fits it
 *     if a real breakpoint-crossing resize later makes it visible. A bounded
 *     rAF retry (capped, see TAB_FIT_MAX_RETRY_FRAMES) is reserved only for
 *     the genuinely-visible instance's transient "not laid out yet" case on
 *     initial load.
 */

(function () {
  'use strict';

  if (customElements.get('o16-product-gallery')) return;

  // ---- Swipe tuning ----
  // SWIPE_THRESHOLD_PX: minimum horizontal travel (px) before a gesture counts as a
  //   swipe-navigate rather than a tap/click — keeps tapping a video etc. working.
  // SWIPE_AXIS_LOCK_PX: how far a pointer must move before we decide whether the
  //   gesture is horizontal (swipe) or vertical (page scroll) — locked once decided.
  const SWIPE_THRESHOLD_PX = 40;
  const SWIPE_AXIS_LOCK_PX = 8;

  // ---- Tab row fit-to-one-line tuning (see class doc comment above) ----
  // TAB_FIT_MAX: natural/base scale — never grown past this.
  // TAB_FIT_MIN: floor scale. Base label font-size is 0.65rem (6.5px at this
  //   theme's 10px root). Below TAB_FIT_MIN_ICONS_KEPT (~5.6px) the icons are
  //   hidden instead of shrinking further (see _fitTabsAttempt) so the label
  //   text — the essential part — never gets uncomfortably small.
  // TAB_FIT_STEP: decrement per iteration of the shrink search.
  // TAB_FIT_RIGHT_RESERVE_PX (owner round-2 2026-07-12, real-phone report):
  //   the tabs container's own box already sits exactly 0.75rem (7.5px at
  //   this theme's 10px root) inside the frame's left+right edges (see CSS).
  //   A fit that merely fills that box exactly would put the last tab's
  //   right edge FLUSH with the box's own right edge — i.e. a right gap of
  //   only ~7.5px in the best case, and on a real device (different font
  //   hinting/kerning than the desktop browser this was authored in) that
  //   can measure a hair narrower still, reading as tighter than the left.
  //   This reserve is subtracted from the available width before the fit
  //   check, so the last tab always stops short of the box's own right edge
  //   by this many px on top of the existing 7.5px inset — i.e. the real
  //   right gap becomes ~7.5 + this value, deliberately >= the left gap.
  const TAB_FIT_MAX               = 1;
  const TAB_FIT_MIN               = 0.78;
  const TAB_FIT_MIN_ICONS_KEPT    = 0.86; // ≈5.6px label font-size (0.65rem × 0.86)
  // Fine step so the discrete search lands close to TAB_FIT_RIGHT_RESERVE_PX
  // rather than overshooting it by several px per iteration (a coarser step
  // was tried first and landed the right gap comfortably past the 10-13px
  // target — see git history).
  const TAB_FIT_STEP              = 0.01;
  const TAB_FIT_RIGHT_RESERVE_PX  = 3;
  const TAB_FIT_RESIZE_DEBOUNCE_MS = 150;
  // Caps the "not laid out yet" rAF retry chain (~0.5s at 60fps) — the
  // CSS-hidden duplicate instance never enters this chain at all (see
  // _fitTabsAttempt), so this only bounds a genuine transient-layout case.
  const TAB_FIT_MAX_RETRY_FRAMES = 30;

  class O16ProductGallery extends HTMLElement {
    constructor() {
      super();
      this._onTabClick      = this._onTabClick.bind(this);
      this._onThumbClick    = this._onThumbClick.bind(this);
      this._onVariantChange = this._onVariantChange.bind(this);
      this._onPointerDown   = this._onPointerDown.bind(this);
      this._onPointerMove   = this._onPointerMove.bind(this);
      this._onPointerUp     = this._onPointerUp.bind(this);
      this._onPointerCancel = this._onPointerCancel.bind(this);
      this._onKeyDown       = this._onKeyDown.bind(this);
      this._onTabsResize    = this._onTabsResize.bind(this);
      this._fitTabs         = this._fitTabs.bind(this);
      this._fitTabsAttempt  = this._fitTabsAttempt.bind(this);
      this._fitTabsRetryFrame = null;
      this._fitTabsRetryCount = 0;
      this._fitTabsResizeTimer = null;
    }

    connectedCallback() {
      this._stage      = this.querySelector('[data-o16-stage]');
      this._mainFrame  = this.querySelector('[data-o16-main-frame]');
      this._tabsEl     = this.querySelector('[data-o16-tabs]');
      this._thumbsEl   = this.querySelector('[data-o16-thumbs]');

      if (!this._mainFrame) return;

      this._tabs   = this._tabsEl   ? Array.from(this._tabsEl.querySelectorAll('[data-tab]'))        : [];
      this._thumbs = this._thumbsEl ? Array.from(this._thumbsEl.querySelectorAll('[data-media-id]')) : [];
      this._items  = Array.from(this._mainFrame.querySelectorAll('[data-media-id]'));

      // ---- Initial state: resolve from markup (Liquid set is-active) ----
      const activeItem = this._items.find(el => el.classList.contains('is-active'));
      this._currentTab     = this.dataset.defaultTab || null;
      this._currentMediaId = activeItem ? activeItem.dataset.mediaId : null;

      // Derive this gallery's section ID from its own element ID:
      // id="o16-gallery-<sectionId>" → strip the "o16-gallery-" prefix.
      this._sectionId = this.id ? this.id.replace(/^o16-gallery-/, '') : null;

      // Apply hidden to non-active-tab thumbs
      this._syncThumbVisibility(this._currentTab, null);

      // ---- "Swipe →" hint (mobile) — see _buildSwipeHint() ----
      // Only built when the active tab's visible set has more than one item
      // (the same set _navigateSwipe itself navigates within), so a
      // single-image product — or a single-item tab — shows no hint.
      this._swipeHintEl    = null;
      this._swipeHintShown = false;
      const initialVisible = this._items.filter(item => this._matchesTab(item, this._currentTab));
      if (initialVisible.length > 1) {
        this._buildSwipeHint();
      }

      // ---- Event listeners ----
      this._tabs.forEach(tab => tab.addEventListener('click', this._onTabClick));
      this._thumbs.forEach(thumb => thumb.addEventListener('click', this._onThumbClick));
      document.addEventListener('variant:change', this._onVariantChange);
      document.addEventListener('keydown', this._onKeyDown);

      // ---- Touch-swipe / mouse-drag on the main image (Pointer Events unify both) ----
      this._swipePointerId = null;
      this._swipeStartX    = 0;
      this._swipeStartY    = 0;
      this._swipeDeltaX    = 0;
      this._swipeAxis      = null; // 'x' (swipe) | 'y' (page scroll) | null (undecided)
      this._mainFrame.addEventListener('pointerdown', this._onPointerDown, { passive: true });

      // Play the active video if the gallery opens on Motion; pause the rest.
      this._syncVideoPlayback();

      // ---- Tab row fit-to-one-line (see class doc comment) ----
      this._fitTabs();
      window.addEventListener('resize', this._onTabsResize);
      window.addEventListener('load', this._fitTabs, { once: true });
      if (document.fonts && typeof document.fonts.ready === 'object' &&
          typeof document.fonts.ready.then === 'function') {
        document.fonts.ready.then(this._fitTabs);
      }
    }

    disconnectedCallback() {
      this._tabs.forEach(tab => tab.removeEventListener('click', this._onTabClick));
      document.removeEventListener('variant:change', this._onVariantChange);
      document.removeEventListener('keydown', this._onKeyDown);
      this._thumbs.forEach(thumb => thumb.removeEventListener('click', this._onThumbClick));
      if (this._mainFrame) {
        this._mainFrame.removeEventListener('pointerdown', this._onPointerDown);
      }
      window.removeEventListener('resize', this._onTabsResize);
      window.removeEventListener('load', this._fitTabs);
      if (this._fitTabsRetryFrame) {
        window.cancelAnimationFrame(this._fitTabsRetryFrame);
        this._fitTabsRetryFrame = null;
      }
      if (this._fitTabsResizeTimer) {
        window.clearTimeout(this._fitTabsResizeTimer);
        this._fitTabsResizeTimer = null;
      }
      this._endSwipeTracking();
    }

    /* ---- SWIPE HINT ("Swipe →") ----------------------------
     * Mirrors o16-card-carousel.js's mobile hint exactly (same non-breaking-
     * space + arrow glyph, same class-suffix fade pattern). Appended as the
     * last child of the main frame so it paints above the (position:absolute,
     * no z-index) media items without needing a z-index bump of its own; CSS
     * scopes it to ≤749px and gives it pointer-events:none so it never
     * intercepts the swipe/drag gesture or a tap.
     * -------------------------------------------------------- */
    _buildSwipeHint() {
      const hintEl = document.createElement('span');
      hintEl.className = 'o16-gallery-swipe-hint';
      hintEl.setAttribute('aria-hidden', 'true');
      // Non-breaking space keeps "Swipe" and "→" from wrapping onto two lines
      // (identical string to o16-card-carousel.js's hint).
      hintEl.textContent = 'Swipe →';
      this._mainFrame.appendChild(hintEl);
      this._swipeHintEl = hintEl;
    }

    /* ---- Visibility test: which items/thumbs belong to a tab ---- */
    _matchesTab(el, cat) {
      if (!cat) return true;
      if (cat === 'all') return el.hasAttribute('data-in-all');
      return el.dataset.cat === cat;
    }

    /* ---- TAB CLICK ---------------------------------------- */
    _onTabClick(e) {
      const tab = e.currentTarget;
      const cat = tab.dataset.tab;
      if (cat === this._currentTab) return;

      // Switch tab active state
      this._tabs.forEach(t => {
        const active = t.dataset.tab === cat;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      this._currentTab = cat;

      // Find first item in this tab's set (backend order for "all")
      const firstItem = this._items.find(el => this._matchesTab(el, cat));
      if (firstItem) {
        this._activateItem(firstItem.dataset.mediaId, cat);
      }

      // Sync thumb visibility
      this._syncThumbVisibility(cat, null);
    }

    /* ---- THUMB CLICK -------------------------------------- */
    _onThumbClick(e) {
      const thumb   = e.currentTarget;
      const mediaId = thumb.dataset.mediaId;
      const cat     = thumb.dataset.cat;

      // If clicking a thumb outside the current tab's set: switch tab first.
      if (!this._matchesTab(thumb, this._currentTab)) {
        const targetTab = cat;
        this._tabs.forEach(t => {
          const active = t.dataset.tab === targetTab;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        this._currentTab = targetTab;
        this._syncThumbVisibility(targetTab, mediaId);
      }

      this._activateItem(mediaId, cat);
    }

    /* ---- SWIPE / DRAG ON THE MAIN IMAGE --------------------
     * Pointer Events unify touch + mouse (+ pen) in one code path. A gesture only
     * navigates once it clears SWIPE_THRESHOLD_PX horizontally AND has been
     * classified as a horizontal gesture (SWIPE_AXIS_LOCK_PX) — so a tap/click
     * (e.g. on a video) never triggers navigation, and a vertical drag is left
     * alone for the page to scroll (also enforced natively by the main frame's
     * `touch-action: pan-y`, which makes the browser hand vertical gestures off
     * to native scrolling and fire us a pointercancel).
     *
     * move/up/cancel are bound on `window` only while a gesture is active (added
     * in _onPointerDown, removed in _endSwipeTracking) — the standard drag
     * pattern — so a mouse-drag that leaves the frame, or a release outside it,
     * is still tracked correctly.
     * -------------------------------------------------------- */
    _onPointerDown(e) {
      // Ignore non-primary mouse buttons and a second simultaneous touch (pinch etc.).
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (this._swipePointerId != null) return;

      this._swipePointerId = e.pointerId;
      this._swipeStartX    = e.clientX;
      this._swipeStartY    = e.clientY;
      this._swipeDeltaX    = 0;
      this._swipeAxis      = null;

      window.addEventListener('pointermove', this._onPointerMove, { passive: true });
      window.addEventListener('pointerup', this._onPointerUp, { passive: true });
      window.addEventListener('pointercancel', this._onPointerCancel, { passive: true });
    }

    _onPointerMove(e) {
      if (e.pointerId !== this._swipePointerId) return;

      const dx = e.clientX - this._swipeStartX;
      const dy = e.clientY - this._swipeStartY;

      if (!this._swipeAxis) {
        if (Math.abs(dx) < SWIPE_AXIS_LOCK_PX && Math.abs(dy) < SWIPE_AXIS_LOCK_PX) return;
        this._swipeAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (this._swipeAxis === 'x' && this._mainFrame) {
          this._mainFrame.classList.add('is-dragging');
        }
      }

      if (this._swipeAxis === 'x') {
        this._swipeDeltaX = dx;
      }
    }

    _onPointerUp(e) {
      if (e.pointerId !== this._swipePointerId) return;

      if (this._swipeAxis === 'x' && Math.abs(this._swipeDeltaX) >= SWIPE_THRESHOLD_PX) {
        // Swipe left (finger/cursor moved toward negative X) → next item; right → previous.
        this._navigateSwipe(this._swipeDeltaX < 0 ? 1 : -1);
      }
      this._endSwipeTracking();
    }

    _onPointerCancel(e) {
      if (e.pointerId !== this._swipePointerId) return;
      this._endSwipeTracking();
    }

    /* ---- ARROW-KEY NAVIGATION (desktop) --------------------
     * ArrowLeft/ArrowRight step through the gallery via the exact same
     * _navigateSwipe() path touch-swipe uses, so tabs, thumbnails, video
     * autoplay and variant state all stay in sync. No visual marker — the
     * "Swipe →" hint stays mobile-only by design (CSS-scoped to ≤749px).
     *
     * Bound on `document` (Left/Right don't scroll the page vertically, so
     * this is safe), but guarded strictly so it never hijacks typing:
     *   - only ArrowLeft / ArrowRight, no modifier keys (ctrl/alt/meta) held
     *   - never fires while an <input>/<textarea>/<select> or a
     *     contenteditable element has focus (quantity field, variant
     *     selects, etc.)
     *   - only the visible/connected gallery instance acts (guards a
     *     detached or currently-hidden gallery elsewhere in the DOM, e.g. a
     *     closed quick-view modal)
     * -------------------------------------------------------- */
    _onKeyDown(e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      const target = e.target;
      const tag = target && target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (target && target.isContentEditable)) {
        return;
      }

      if (!this.isConnected || (this.offsetWidth === 0 && this.offsetHeight === 0)) return;

      e.preventDefault();
      this._navigateSwipe(e.key === 'ArrowRight' ? 1 : -1);
    }

    _endSwipeTracking() {
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
      window.removeEventListener('pointercancel', this._onPointerCancel);
      if (this._mainFrame) {
        this._mainFrame.classList.remove('is-dragging');
      }
      this._swipePointerId = null;
      this._swipeStartX    = 0;
      this._swipeStartY    = 0;
      this._swipeDeltaX    = 0;
      this._swipeAxis      = null;
    }

    /* Move to the adjacent item WITHIN the currently visible set (the same items
     * the thumbnail row shows for the active tab — see _matchesTab), then hand
     * off to the exact same _activateItem() thumbnail clicks use, so tabs,
     * thumbnails, aria state and video autoplay all stay in sync automatically.
     * direction: 1 = next, -1 = previous. Clamps at the ends (no loop), matching
     * the existing thumbnail navigation (which has no loop either). Reused by
     * both touch-swipe (_onPointerUp) and desktop ArrowLeft/ArrowRight
     * (_onKeyDown) — a single navigation path keeps everything in sync. */
    _navigateSwipe(direction) {
      const visible = this._items.filter(item => this._matchesTab(item, this._currentTab));
      if (visible.length < 2) return;

      const currentIndex = visible.findIndex(item => item.dataset.mediaId === this._currentMediaId);
      if (currentIndex === -1) return;

      const nextIndex = currentIndex + direction;
      if (nextIndex < 0 || nextIndex >= visible.length) return; // clamp — no loop

      const target = visible[nextIndex];
      this._activateItem(target.dataset.mediaId, target.dataset.cat);

      // First genuine navigation via this path: fade the "Swipe →" hint once
      // (owner: "disappears once they've swiped the first time"). A tap or a
      // thumbnail click calls _activateItem() directly and never reaches here,
      // so those never hide it — only a real swipe (or an arrow-key step,
      // harmless since the hint is CSS-hidden on desktop) does.
      if (!this._swipeHintShown && this._swipeHintEl) {
        this._swipeHintEl.classList.add('o16-gallery-swipe-hint--hidden');
        this._swipeHintShown = true;
      }
    }

    /* ---- ACTIVATE ITEM ------------------------------------ */
    _activateItem(mediaId, cat) {
      if (mediaId === this._currentMediaId) return;

      // Main stage items
      this._items.forEach(item => {
        const active = item.dataset.mediaId === mediaId;
        item.classList.toggle('is-active', active);
      });

      // Thumbnails
      this._thumbs.forEach(thumb => {
        const active = thumb.dataset.mediaId === mediaId;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      this._currentMediaId = mediaId;

      // Start/stop any video to match the newly active item.
      this._syncVideoPlayback();

      // Scroll the active thumb into view (both orientations)
      const activeThumb = this._thumbs.find(t => t.dataset.mediaId === mediaId);
      if (activeThumb && this._thumbsEl) {
        activeThumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
    }

    /* ---- VIDEO PLAYBACK ----------------------------------- */
    /* Play the active item's video (muted autoplay + loop), pause all others. */
    _syncVideoPlayback() {
      this._items.forEach(item => {
        const video = item.querySelector('video');
        if (!video) return;
        if (item.classList.contains('is-active')) {
          const p = video.play();
          if (p && typeof p.catch === 'function') {
            p.catch(() => {/* autoplay blocked — video stays paused on its poster */});
          }
        } else if (!video.paused) {
          video.pause();
        }
      });
    }

    /* ---- SYNC THUMB VISIBILITY --------------------------- */
    _syncThumbVisibility(cat, preferredMediaId) {
      let firstVisibleThumb = null;

      this._thumbs.forEach(thumb => {
        const visible = this._matchesTab(thumb, cat);
        if (visible) {
          thumb.removeAttribute('hidden');
          if (!firstVisibleThumb) firstVisibleThumb = thumb;
        } else {
          thumb.setAttribute('hidden', '');
          thumb.classList.remove('is-active');
          thumb.setAttribute('aria-pressed', 'false');
        }
      });

      // Set active thumb: preferredMediaId if given, else first visible
      const targetId = preferredMediaId ||
        (firstVisibleThumb ? firstVisibleThumb.dataset.mediaId : null);

      if (targetId) {
        this._thumbs.forEach(thumb => {
          if (thumb.hasAttribute('hidden')) return;
          const active = thumb.dataset.mediaId === targetId;
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      }
    }

    /* ---- TAB ROW FIT-TO-ONE-LINE --------------------------
     * See the class doc comment for the full contract. Debounced entry point
     * for the window 'resize' listener.
     * -------------------------------------------------------- */
    _onTabsResize() {
      if (this._fitTabsResizeTimer) window.clearTimeout(this._fitTabsResizeTimer);
      this._fitTabsResizeTimer = window.setTimeout(this._fitTabs, TAB_FIT_RESIZE_DEBOUNCE_MS);
    }

    /* Real content width of the tab row: first tab's left edge to last tab's
     * right edge. (The tabs container itself does NOT auto-size to content —
     * its left+right insets in CSS give it a fixed box — so its own
     * getBoundingClientRect().width is the AVAILABLE width, not the content
     * width; flex-shrink:0 + white-space:nowrap children can overflow that
     * fixed box, which is exactly the condition this measures.) */
    _tabsContentWidth() {
      const first = this._tabs[0].getBoundingClientRect();
      const last  = this._tabs[this._tabs.length - 1].getBoundingClientRect();
      return last.right - first.left;
    }

    /* Public entry point — resets the bounded retry budget, then runs one
     * fit attempt. Call this (not _fitTabsAttempt) from every EXTERNAL
     * trigger (connect, resize, load, fonts.ready) so each fresh trigger
     * gets its own full retry budget; the attempt's own rAF self-recursion
     * (transient "not laid out yet" case) calls _fitTabsAttempt directly so
     * it consumes that same budget instead of resetting it every frame. */
    _fitTabs() {
      this._fitTabsRetryCount = 0;
      this._fitTabsAttempt();
    }

    _fitTabsAttempt() {
      if (!this._tabsEl || this._tabs.length < 2) return; // 0-1 tabs never overflow this way

      // Reset to natural size first — this routine must also GROW back (a
      // resize to a wider viewport, or a hidden duplicate instance becoming
      // the visible one) as well as shrink, so we always re-derive from the
      // base size rather than only ever tightening further.
      this._tabsEl.style.setProperty('--o16-tab-fit', String(TAB_FIT_MAX));
      this._tabsEl.classList.remove('o16-gallery__tabs--icons-hidden');

      const available = this._tabsEl.getBoundingClientRect().width;
      if (available <= 0) {
        // This page renders the gallery section TWICE — one instance for
        // ≤749px, one for ≥750px (see class doc comment) — purely via CSS
        // `display:none` on the wrapper at the other breakpoint, not via JS.
        // If THIS instance is display:none (offsetParent null), it is not
        // "about to become visible" — on most sessions (a phone that never
        // crosses 750px) it never will. Do NOT poll via rAF in that case:
        // that would spin every single frame for the entire page visit. The
        // window 'resize' listener already re-invokes _fitTabs() (which
        // resets this budget) if a real breakpoint-crossing resize later
        // makes this instance visible — that is the only trigger it needs.
        if (this._tabsEl.offsetParent === null) return;

        // Otherwise this is the genuinely-visible instance, just not laid
        // out yet (e.g. first paint still settling on initial page load) —
        // bounded retry so a pathological case can never spin forever;
        // window 'load' / document.fonts.ready are the safety net if this
        // budget is exhausted before layout settles.
        if (this._fitTabsRetryCount < TAB_FIT_MAX_RETRY_FRAMES) {
          this._fitTabsRetryCount++;
          if (this._fitTabsRetryFrame) window.cancelAnimationFrame(this._fitTabsRetryFrame);
          this._fitTabsRetryFrame = window.requestAnimationFrame(this._fitTabsAttempt);
        }
        return;
      }

      // Explicit reserved target — NOT the container's raw width. The
      // container's own box already sits exactly 0.75rem inside the frame's
      // edges (CSS), so fitting content flush to `available` would leave the
      // last tab's right edge exactly at that inset — a right gap that reads
      // as tight/equal-at-best, and on a real device's font metrics can
      // measure narrower than the left. Subtracting the reserve guarantees
      // the real right gap lands a few px BEYOND the existing 0.75rem inset,
      // deliberately ≥ the left gap on every device (owner round-2 2026-07-12).
      const target = available - TAB_FIT_RIGHT_RESERVE_PX;

      // Pass 1 — shrink font-size/padding/icon-size (icons still visible)
      // down to TAB_FIT_MIN_ICONS_KEPT (~5.6px label, the floor the owner
      // still considers comfortably legible).
      if (this._fitTabsAtScale(target, TAB_FIT_MAX, TAB_FIT_MIN_ICONS_KEPT)) return;

      // Pass 2 — icons still didn't leave enough room even at that floor.
      // Hide the decorative icons (≤749px only, via CSS) to buy back ~16px
      // per tab, then re-attempt from full scale down to the absolute floor
      // — the label text is the essential, always-visible part.
      this._tabsEl.classList.add('o16-gallery__tabs--icons-hidden');
      this._fitTabsAtScale(target, TAB_FIT_MAX, TAB_FIT_MIN);
    }

    /* Shrink --o16-tab-fit from fromScale down to minScale (in TAB_FIT_STEP
     * increments), stopping as soon as the row's content width fits within
     * `target`. Returns true if a fitting scale was found (or it already fit
     * at fromScale), false if even minScale still overflows `target` — the
     * caller decides the next fallback (e.g. hiding icons) in that case. */
    _fitTabsAtScale(target, fromScale, minScale) {
      this._tabsEl.style.setProperty('--o16-tab-fit', String(fromScale));
      if (this._tabsContentWidth() <= target) return true;

      let scale = fromScale;
      while (scale > minScale + 0.0001) {
        scale = Math.max(minScale, scale - TAB_FIT_STEP);
        this._tabsEl.style.setProperty('--o16-tab-fit', String(scale));
        if (this._tabsContentWidth() <= target) return true;
      }
      return false;
    }

    /* ---- VARIANT CHANGE -----------------------------------
     * Responds to the base-theme "variant:change" CustomEvent (fired by
     * VariantSelects / VariantRadios in global.js whenever the visitor
     * selects a variant). Jumps the gallery to the variant's featured
     * media item and makes it the active primary image.
     *
     * The item is NOT removed from the gallery — it stays as a normal
     * navigable item inside its tab (All / Studio / In-use), so the
     * visitor can page away and back to it without re-selecting the variant.
     *
     * Tab-switch logic:
     *   1. If the item has data-in-all AND the "all" tab exists → switch to "all".
     *   2. Else → switch to the item's own category tab (inuse / studio / motion).
     *   3. If we are already on the correct tab → just activate the item directly.
     * -------------------------------------------------------- */
    _onVariantChange(e) {
      // Only respond to events from our own section (guards multi-section pages).
      if (!this._sectionId || !e.detail || e.detail.sectionId !== this._sectionId) return;

      const variant = e.detail.variant;
      if (!variant || !variant.featured_media) return;

      const mediaId = String(variant.featured_media.id);

      // Find the gallery item with this media ID.
      const targetItem = this._items.find(el => el.dataset.mediaId === mediaId);
      if (!targetItem) return;

      // Determine the best tab to show this item in:
      //   - Prefer "all" if the item is in it (has data-in-all) and the all-tab exists.
      //   - Otherwise fall back to the item's own category tab.
      const itemCat = targetItem.dataset.cat || null;
      const allTabEl = this._tabs.find(t => t.dataset.tab === 'all');
      let targetTab;
      if (allTabEl && targetItem.hasAttribute('data-in-all')) {
        targetTab = 'all';
      } else if (itemCat) {
        targetTab = itemCat;
      } else {
        targetTab = this._currentTab;
      }

      // Switch tab if needed, then activate the item.
      if (targetTab && targetTab !== this._currentTab) {
        this._tabs.forEach(t => {
          const active = t.dataset.tab === targetTab;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        this._currentTab = targetTab;
        this._syncThumbVisibility(targetTab, mediaId);
      }

      // Force-activate even if mediaId === _currentMediaId (variant re-select).
      this._currentMediaId = null;
      this._activateItem(mediaId, targetTab || itemCat);
    }
  }

  customElements.define('o16-product-gallery', O16ProductGallery);
})();
